/**
 * First-run guided tour of outcomes.lol (#776). Shown automatically on the first visit, skippable at every step, resumed
 * after a reload, replayable from the Help menu. It only navigates and points at real UI: it never places an order,
 * never opens a wallet prompt and never sends anything to /exchange.
 *
 *  - Market steps open a live TESTNET market (switching a mainnet viewer to testnet for the tour, restored afterwards).
 *  - The portfolio step shows the connected wallet, or ghost mode on a public address when none is connected
 *    (ghost mode is left again when the tour moves on or ends).
 *
 * Must be rendered inside the Router, EnvProvider, Hl/Query providers, WalletProvider and GhostProvider.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEnv } from '../config/env';
import { useOutcomeMeta } from '../catalog/useOutcomeMeta';
import { useAllMids } from '../hl/mids';
import { parseOutcomeId } from '../market/outcomeId';
import { scopeOutcomes } from '../venue/scope';
import { useVenueScope } from '../venue/useVenueScope';
import { useGhost, useViewedAccount, useWallet } from '../wallet/context';
import { TOUR_COPY } from './copy';
import { tDynamic } from '../i18n/i18n';
import { useT } from '../i18n/useT';
import { TourContext, type TourContextValue } from './context';
import { initialAction, initialTourState, toPersisted, tourReducer, type TourAction } from './machine';
import { pickTourMarket } from './pickMarket';
import { LEARN_PATH, TOUR_GHOST_ADDRESS, TOUR_STEPS } from './steps';
import { loadTour, saveTour } from './storage';
import TourOverlay from './TourOverlay';
import { useGeo } from '../geo/context';

export function TourProvider({
  children,
  autoStart = import.meta.env.MODE !== 'test',
}: {
  children: ReactNode;
  /** Auto-start on a first visit / resume an interrupted tour. Off in unit tests (MODE "test"). */
  autoStart?: boolean;
}) {
  const [state, dispatch] = useReducer(tourReducer, TOUR_STEPS.length, initialTourState);

  // #769: wait until the terms of use are accepted, so the tour never opens under the terms dialog.
  const { termsAccepted } = useGeo();
  useEffect(() => {
    if (!autoStart || !termsAccepted) return;
    const a = initialAction(loadTour(), TOUR_STEPS.length);
    if (a) dispatch(a);
  }, [autoStart, termsAccepted]);

  useEffect(() => {
    const p = toPersisted(state);
    if (p) saveTour(p);
  }, [state]);

  const replay = useCallback(() => dispatch({ type: 'start', index: 0 }), []);
  const active = state.status === 'running';
  const value = useMemo<TourContextValue>(() => ({ active, replay }), [active, replay]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && <TourRunner index={state.index} dispatch={dispatch} />}
    </TourContext.Provider>
  );
}

/** Mounted only while the tour runs: owns navigation for each step and renders the overlay. */
function TourRunner({ index, dispatch }: { index: number; dispatch: (a: TourAction) => void }) {
  const step = TOUR_STEPS[index]!;
  const t = useT();
  // Words come from the catalog (tour.step.<snake id>.*), which mirrors copy.ts; copy.ts gives the paragraph count.
  const ns = `tour.step.${step.id.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)}`;
  const copy = {
    title: tDynamic(`${ns}.title`),
    body: TOUR_COPY[step.id].body.map((_, i) => tDynamic(`${ns}.body_${i + 1}`)),
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { env, setEnv } = useEnv();
  const { exitGhost } = useGhost();
  const viewed = useViewedAccount();
  const { status: walletStatus } = useWallet();
  const meta = useOutcomeMeta();
  const { mids } = useAllMids();
  const { venue: scopeVenue } = useVenueScope();

  // Latest values for effects that must not re-run on every location / account change.
  const live = useRef({ location, viewed, exitGhost, setEnv, navigate });
  live.current = { location, viewed, exitGhost, setEnv, navigate };

  const envAtStart = useRef(env.name);
  const switchedEnv = useRef(false);
  const ghostByTour = useRef(false);

  const outcomes = useMemo(() => scopeOutcomes(meta.data?.outcomes ?? [], scopeVenue), [meta.data, scopeVenue]);
  const metaReady = meta.data !== undefined;
  const picked = useMemo(() => pickTourMarket(outcomes, mids), [outcomes, mids]);
  // Lock the market for the whole tour once chosen (mids keep moving).
  const marketRef = useRef<number | null>(null);
  // Right after page load (auto-start / resume), give allMids a moment to arrive so the pick can prefer a live book,
  // and a remembered wallet a moment to reconnect so the portfolio step does not fall back to ghost mode.
  const [bootWaitOver, setBootWaitOver] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setBootWaitOver(true), 1500);
    return () => window.clearTimeout(t);
  }, []);
  const midsReady = mids !== undefined || bootWaitOver;
  const walletSettled = bootWaitOver && walletStatus !== 'connecting' && walletStatus !== 'reconnecting';

  useEffect(() => {
    const { location: loc, viewed: acct, exitGhost: leaveGhost, setEnv: switchEnv, navigate: go } = live.current;
    const page = step.page;
    if (page !== 'portfolio' && ghostByTour.current) {
      ghostByTour.current = false;
      leaveGhost();
    }
    if (page === 'catalog') {
      if (loc.pathname !== '/') go('/');
    } else if (page === 'market') {
      if (env.name !== 'testnet') {
        switchedEnv.current = true;
        switchEnv('testnet');
        return; // re-runs with the testnet catalog
      }
      if (!metaReady) return;
      const current = loc.pathname.startsWith('/market/') ? parseOutcomeId(loc.pathname.slice('/market/'.length)) : null;
      if (current !== null && outcomes.some((o) => o.outcome === current)) {
        marketRef.current = current;
        return;
      }
      if (marketRef.current === null && !midsReady) return;
      const id = marketRef.current ?? picked;
      if (id === null) return; // nothing listed: the steps fall back to centred cards
      marketRef.current = id;
      go(`/market/${id}`);
    } else if (page === 'portfolio') {
      if (loc.pathname === '/portfolio') return;
      if (acct.mode === 'none' && !walletSettled) return;
      if (acct.mode === 'none') {
        ghostByTour.current = true;
        go(`/portfolio?ghost=${TOUR_GHOST_ADDRESS}`);
      } else {
        go('/portfolio');
      }
    }
    // `picked` is deliberately read only when a market has not been chosen yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, step.page, env.name, metaReady, midsReady, walletSettled, outcomes]);

  // Tour ended (finished, skipped) or replaced: undo what the tour changed.
  useEffect(
    () => () => {
      const { location: loc, exitGhost: leaveGhost, setEnv: switchEnv, navigate: go } = live.current;
      if (ghostByTour.current) {
        ghostByTour.current = false;
        leaveGhost();
      }
      if (switchedEnv.current && envAtStart.current !== 'testnet') {
        switchedEnv.current = false;
        switchEnv(envAtStart.current);
        // Outcome ids are per network: a testnet market page would be "not found" (#707).
        if (loc.pathname.startsWith('/market/')) go('/');
      }
    },
    [],
  );

  const next = useCallback(() => dispatch({ type: 'next' }), [dispatch]);
  const back = useCallback(() => dispatch({ type: 'back' }), [dispatch]);
  const skip = useCallback(() => dispatch({ type: 'skip' }), [dispatch]);

  const extra =
    step.id === 'finish' ? (
      <p className="text-sm">
        <Link
          to={LEARN_PATH}
          onClick={() => dispatch({ type: 'finish' })}
          className="font-medium text-accent hover:underline"
          data-testid="tour-learn"
        >
          {t('tour.ui.learn')}{' '}
          <span aria-hidden className="flip-rtl">
            →
          </span>
        </Link>
      </p>
    ) : undefined;

  return (
    <TourOverlay
      stepKey={step.id}
      index={index}
      total={TOUR_STEPS.length}
      targets={step.targets}
      title={copy.title}
      body={copy.body}
      extra={extra}
      onNext={next}
      onBack={back}
      onSkip={skip}
    />
  );
}
